import _ from "lodash";
import React, { useEffect, useState } from "react";

export enum SelectAllState {
  ALL = "ALL", // Select all items, including ones not rendered on screen
  RENDERED = "RENDERED", // Select all items currently rendered on screen
  NOT_SELECTED = "NOT_SELECTED", // Select all is not selected
}

function isSelectAllEnabled(selectAll: SelectAllState): boolean {
  switch (selectAll) {
    case SelectAllState.ALL:
    case SelectAllState.RENDERED:
      return true;
    case SelectAllState.NOT_SELECTED:
      return false;
  }
}

type BulkActionsStateType = { selectedItems: Map<string, boolean>; selectAll: SelectAllState };

const initialBulkActionsState: BulkActionsStateType = { selectedItems: new Map(), selectAll: SelectAllState.NOT_SELECTED };

const BulkActionsContext = React.createContext<[BulkActionsStateType, (_: BulkActionsStateType) => void]>([
  initialBulkActionsState,
  (_: BulkActionsStateType) => undefined,
]);

function BulkActionsProvider({ itemIds, ...props }: { itemIds: Set<string> } & { [key: string]: any }) {
  const initialSelectedItems = new Map([...itemIds].map((id) => [id, false]));
  const [bulkActionsState, setBulkActionsState] = useState<BulkActionsStateType>({
    ...initialBulkActionsState,
    selectedItems: initialSelectedItems,
  });

  // If the available list of itemIds changes, we need to update the selectedItems map to only include those items
  useEffect(() => {
    const { selectAll, selectedItems } = bulkActionsState;
    const newSelectedItems = new Map([...itemIds].map((id) => [id, selectedItems.get(id) ?? isSelectAllEnabled(selectAll)]));

    setBulkActionsState({ ...bulkActionsState, selectedItems: newSelectedItems, selectAll });
  }, [itemIds]);

  return <BulkActionsContext.Provider value={[bulkActionsState, setBulkActionsState]} {...props} />;
}

function useBulkActionsState(): [BulkActionsStateType, (_: BulkActionsStateType) => void] {
  const context = React.useContext(BulkActionsContext);
  if (context === undefined) {
    throw new Error(`useBulkActionsState must be used within a BulkEditProvider`);
  }
  return context;
}

/**
 * Returns a map of items where the key is the item id and the value is whether or not it is selected
 */
function useSelectedItems(): [Map<string, boolean>, (_: Map<string, boolean>) => void] {
  const [{ selectedItems, ...state }, setBulkActionsState] = useBulkActionsState();
  const setSelectedItems = (newselectedItems: Map<string, boolean>) => {
    setBulkActionsState({ ...state, selectedItems: newselectedItems });
  };

  return [selectedItems, setSelectedItems];
}

/**
 * Returns the keys of all the items where selected = true
 */
function useSelectedKeys(): string[] {
  const [{ selectedItems }] = useBulkActionsState();
  return _.entries(selectedItems)
    .filter(([_, value]) => value)
    .map(([key, _]) => key);
}

function useSelected(id: string): [boolean, (_: boolean) => void] {
  const [{ selectedItems, selectAll, ...state }, setBulkActionsState] = useBulkActionsState();
  // This ensures that when a new item is rendered, it takes into account the selectAllEnabled state
  const selected = selectedItems.get(id) === true || isSelectAllEnabled(selectAll);

  const setSelected = (newSelected: boolean) => {
    const newselectedItems = new Map(selectedItems);
    newselectedItems.set(id, newSelected);
    const allItemsSelected = Array.from(newselectedItems.values()).every((value) => value);

    let newSelectAll: SelectAllState;
    if (allItemsSelected && selectAll === SelectAllState.NOT_SELECTED) {
      // If all items are selected and the selectAll state is not selected, then we want to set the selectAll state to RENDERED
      newSelectAll = SelectAllState.RENDERED;
    } else if (!allItemsSelected) {
      // If all items are not selected, then we want to set the selectAll state to NOT_SELECTED
      newSelectAll = SelectAllState.NOT_SELECTED;
    } else {
      // Otherwise, we want to keep the current selectAll state
      newSelectAll = selectAll;
    }

    setBulkActionsState({ ...state, selectedItems: newselectedItems, selectAll: newSelectAll });
  };

  return [selected, setSelected];
}

function useDeselectAll() {
  const [{ selectedItems, ...state }, setBulkActionsState] = useBulkActionsState();

  const deselectAll = () => {
    const newSelectedItems = new Map([...selectedItems.keys()].map((key) => [key, false]));
    setBulkActionsState({ ...state, selectedItems: newSelectedItems, selectAll: SelectAllState.NOT_SELECTED });
  };

  return deselectAll;
}

function useSelectAll() {
  const [{ selectAll, selectedItems, ...state }, setBulkActionsState] = useBulkActionsState();

  const setSelectAll = (newValue: SelectAllState) => {
    const newSelectionState = isSelectAllEnabled(newValue);
    const newSelectedItems = new Map([...selectedItems.keys()].map((key) => [key, newSelectionState]));
    setBulkActionsState({ ...state, selectedItems: newSelectedItems, selectAll: newValue });
  };

  const flipSelectAll = () => {
    if (selectAll === SelectAllState.NOT_SELECTED) {
      setSelectAll(SelectAllState.RENDERED);
      return;
    }

    setSelectAll(SelectAllState.NOT_SELECTED);
  };

  const selectAllEnabled = isSelectAllEnabled(selectAll);

  return { selectAll, selectAllEnabled, setSelectAll, flipSelectAll };
}

export { BulkActionsProvider, useBulkActionsState, useSelectedItems, useSelectedKeys, useSelected, useSelectAll, useDeselectAll };
